import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { message } from 'antd';
import {
  ArrowLeft, FolderOpen, Folder, Plus, Upload,
  Trash2, Image, ChevronRight, Layers, Check, Pencil, FolderOpen as FolderMove,
} from 'lucide-react';
import { V, cardStyle, h2Style, labelStyle, primaryBtnStyle, secondaryBtnStyle } from './vividGlass';
import LazyImage from '../../../components/common/LazyImage';
import brandAssetActions from '../../../actions/brandAssets';
import brandDnaActions from '../../../actions/brandDna';
import AssetUploadModal from './components/AssetUploadModal';
import AssetDetailPanel from './components/AssetDetailPanel';
import { ASSET_TYPES_CONFIG, ASSET_TYPE_MAP, CAPTION_STATUS } from './components/assetConstants';

export default function BrandAssetsScreen() {
  const { brandId } = useParams();
  const navigate = useNavigate();

  const [brand, setBrand] = useState(null);
  const [folders, setFolders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [pagination, setPagination] = useState({ total: 0 });
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [moveFolderOpen, setMoveFolderOpen] = useState(false);
  const [folderCounts, setFolderCounts] = useState({});

  const fetchBrand = useCallback(async () => {
    try {
      const res = await brandDnaActions.getBrand(brandId);
      if (res.success) setBrand(res.data);
    } catch { navigate('/brand-dna'); }
  }, [brandId, navigate]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await brandAssetActions.listFolders(brandId);
      if (res.success) setFolders(res.data || []);
    } catch { /* silent */ }
  }, [brandId]);

  const fetchFolderCounts = useCallback(async () => {
    try {
      const res = await brandAssetActions.getFolderCounts(brandId);
      if (res.success) setFolderCounts(res.data || {});
    } catch { /* silent */ }
  }, [brandId]);

  const fetchAssets = useCallback(async () => {
    try {
      setLoadingAssets(true);
      const params = { page: 1, limit: 50 };
      if (activeFolderId) params.folder_id = activeFolderId;
      if (filterType) params.asset_type = filterType;
      const res = await brandAssetActions.listAssets(brandId, params);
      if (res.success) {
        setAssets(res.data || []);
        setPagination(res.pagination || { total: 0 });
      }
    } catch { /* silent */ } finally { setLoadingAssets(false); }
  }, [brandId, activeFolderId, filterType]);

  useEffect(() => { fetchBrand(); fetchFolders(); fetchFolderCounts(); }, [fetchBrand, fetchFolders, fetchFolderCounts]);
  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await brandAssetActions.createFolder(brandId, { name: newFolderName.trim() });
      setNewFolderName(''); setShowNewFolder(false); fetchFolders();
      message.success('Folder created');
    } catch { message.error('Failed to create folder'); }
  };

  const handleRenameFolder = async (folderId, newName) => {
    if (!newName.trim()) return;
    try {
      await brandAssetActions.updateFolder(brandId, folderId, { name: newName.trim() });
      fetchFolders();
    } catch { message.error('Failed to rename folder'); }
  };

  const handleDeleteFolder = async (folderId, e) => {
    e.stopPropagation();
    try {
      await brandAssetActions.deleteFolder(brandId, folderId);
      if (activeFolderId === folderId) setActiveFolderId(null);
      fetchFolders(); fetchAssets();
    } catch { message.error('Failed to delete folder'); }
  };

  const handleDeleteFromCard = async (assetId) => {
    try {
      await brandAssetActions.deleteAsset(brandId, assetId);
      setAssets((prev) => prev.filter((a) => a.asset_id !== assetId));
      if (selectedAsset?.asset_id === assetId) setSelectedAsset(null);
    } catch { message.error('Failed to remove asset'); }
  };

  const handleAssetDeleted = (assetId) => {
    setAssets((prev) => prev.filter((a) => a.asset_id !== assetId));
    if (selectedAsset?.asset_id === assetId) setSelectedAsset(null);
  };

  const handleAssetUpdated = (updated) => {
    setAssets((prev) => prev.map((a) => a.asset_id === updated.asset_id ? updated : a));
    if (selectedAsset?.asset_id === updated.asset_id) setSelectedAsset(updated);
  };

  const toggleSelect = (assetId, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(assetId) ? next.delete(assetId) : next.add(assetId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    try {
      await Promise.all(ids.map((id) => brandAssetActions.deleteAsset(brandId, id)));
      setAssets((prev) => prev.filter((a) => !ids.includes(a.asset_id)));
      clearSelection();
      message.success(`${ids.length} asset${ids.length !== 1 ? 's' : ''} deleted`);
    } catch { message.error('Some deletions failed'); }
  };

  const handleBulkMove = async (folderId) => {
    const ids = [...selectedIds];
    try {
      await Promise.all(ids.map((id) => brandAssetActions.updateAsset(brandId, id, { folder_id: folderId })));
      setMoveFolderOpen(false);
      clearSelection();
      fetchAssets();
      fetchFolderCounts();
      message.success(`Moved ${ids.length} asset${ids.length !== 1 ? 's' : ''}`);
    } catch { message.error('Move failed'); }
  };

  const inputStyle = {
    width: '100%', padding: '6px 10px', borderRadius: 8, boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.06)', border: `1px solid ${V.glassBorder}`,
    color: V.text, fontSize: 13, outline: 'none', fontFamily: V.font,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', background: 'transparent', fontFamily: V.font, position: 'relative' }}>
      {/* Fixed background — never scrolls, no repaint on scroll */}
      <div style={{ position: 'fixed', inset: 0, background: V.bgGradient, zIndex: -1, pointerEvents: 'none' }} />
      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.35}}
        .asset-hover-overlay{position:absolute;inset:0;background:rgba(0,0,0,0);transition:background 0.15s;pointer-events:none;}
        .asset-card-wrap:hover .asset-hover-overlay{background:rgba(0,0,0,0.45);}
      `}</style>

      <div style={{ maxWidth: 1440, margin: '0 auto', width: '100%', padding: '28px 32px 0', boxSizing: 'border-box' }}>
        {/* Breadcrumb / back */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
          <button type="button" onClick={() => navigate(`/brand-dna/${brandId}`)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: V.textMuted, fontSize: 13, cursor: 'pointer', fontFamily: V.font, padding: 0 }}>
            <ArrowLeft size={14} /> {brand?.brand_name || 'Brand'}
          </button>
          <ChevronRight size={13} color="rgba(255,255,255,0.2)" />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: V.text }}>
            <Layers size={14} color={V.primary} /> Asset Library
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: V.textMuted }}>
            {pagination.total} asset{pagination.total !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Main content: sidebar + grid */}
      <div style={{ display: 'flex', flex: 1, maxWidth: 1440, margin: '0 auto', width: '100%', padding: '0 32px 48px', boxSizing: 'border-box', gap: 24, alignItems: 'flex-start' }}>

        {/* Folder sidebar */}
        <div style={{ width: 220, flexShrink: 0, ...cardStyle, padding: 16 }}>
          <div style={{ ...labelStyle, marginBottom: 10 }}>Folders</div>

          <FolderRow
            label="All Assets"
            count={pagination.total}
            active={activeFolderId === null}
            onClick={() => setActiveFolderId(null)}
            icon={<Image size={13} color={activeFolderId === null ? V.primary : V.textMuted} />}
          />
          {folders.map((f) => (
            <FolderRow
              key={f.folder_id}
              label={f.name}
              count={folderCounts[f.folder_id] || 0}
              active={activeFolderId === f.folder_id}
              onClick={() => setActiveFolderId(f.folder_id)}
              onDelete={(e) => handleDeleteFolder(f.folder_id, e)}
              onRename={(name) => handleRenameFolder(f.folder_id, name)}
            />
          ))}

          {showNewFolder && (
            <div style={{ marginTop: 6 }}>
              <input
                autoFocus value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); } }}
                placeholder="Folder name"
                style={{ ...inputStyle, marginBottom: 6 }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={handleCreateFolder}
                  style={{ flex: 1, padding: '5px 0', background: 'linear-gradient(90deg,#FF7E67,#FF4D8D)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: V.font }}>
                  Create
                </button>
                <button type="button" onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}
                  style={{ flex: 1, padding: '5px 0', background: 'none', border: `1px solid ${V.glassBorder}`, borderRadius: 8, color: V.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: V.font }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <button type="button" onClick={() => setShowNewFolder(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', marginTop: 10, padding: '7px 10px', background: 'transparent', border: `1px dashed ${V.glassBorder}`, borderRadius: 10, color: V.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: V.font }}>
            <Plus size={11} /> New Folder
          </button>
        </div>

        {/* Right: filters + grid */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Top bar: type filters + upload */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, flexWrap: 'wrap' }}>
              <FilterChip active={filterType === ''} color={V.primary} onClick={() => setFilterType('')}>All</FilterChip>
              {ASSET_TYPES_CONFIG.filter((t) => t.value !== 'auto').map((t) => (
                <FilterChip key={t.value} active={filterType === t.value} color={t.color} onClick={() => setFilterType(filterType === t.value ? '' : t.value)}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: t.color, display: 'inline-block', marginRight: 5, flexShrink: 0 }} />
                  {t.label}
                </FilterChip>
              ))}
            </div>
            <button type="button" onClick={() => setShowUpload(true)} style={{ ...primaryBtnStyle, padding: '9px 18px', fontSize: 13 }}>
              <Upload size={13} /> Upload
            </button>
          </div>

          {/* Asset grid */}
          {loadingAssets ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
              {[...Array(12)].map((_, i) => (
                <div key={i} style={{ aspectRatio: '4/3', borderRadius: 16, background: 'rgba(255,255,255,0.05)', animation: 'pulse 1.5s ease-in-out infinite' }} />
              ))}
            </div>
          ) : assets.length === 0 ? (
            <EmptyAssets onUpload={() => setShowUpload(true)} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
              {assets.map((asset) => (
                <AssetCard
                  key={asset.asset_id}
                  asset={asset}
                  onDelete={() => handleDeleteFromCard(asset.asset_id)}
                  onClick={(e) => {
                    if (selectedIds.size > 0) { toggleSelect(asset.asset_id, e); }
                    else setSelectedAsset(asset);
                  }}
                  selected={selectedIds.has(asset.asset_id)}
                  onSelect={(e) => toggleSelect(asset.asset_id, e)}
                  selectionMode={selectedIds.size > 0}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUpload && (
          <AssetUploadModal
            brandId={brandId}
            folderId={activeFolderId}
            folders={folders}
            onClose={() => setShowUpload(false)}
            onDone={() => { setShowUpload(false); fetchAssets(); fetchFolderCounts(); }}
          />
        )}
      </AnimatePresence>

      {/* Bulk selection bar */}
      <AnimatePresence>
        {selectedIds.size > 0 && (
          <SelectionBar
            count={selectedIds.size}
            folders={folders}
            moveFolderOpen={moveFolderOpen}
            setMoveFolderOpen={setMoveFolderOpen}
            onMove={handleBulkMove}
            onDelete={handleBulkDelete}
            onClear={clearSelection}
          />
        )}
      </AnimatePresence>

      {/* Detail panel */}
      <AnimatePresence>
        {selectedAsset && (
          <AssetDetailPanel
            asset={selectedAsset}
            brandId={brandId}
            onClose={() => setSelectedAsset(null)}
            onDeleted={handleAssetDeleted}
            onUpdated={handleAssetUpdated}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Folder row ────────────────────────────────────────────────
function FolderRow({ label, count, active, onClick, onDelete, onRename, icon }) {
  const [hov, setHov] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(label);

  const commitRename = () => {
    setEditing(false);
    if (editVal.trim() && editVal.trim() !== label) onRename?.(editVal.trim());
    else setEditVal(label);
  };

  if (editing) {
    return (
      <div style={{ padding: '3px 2px' }}>
        <input
          autoFocus value={editVal}
          onChange={(e) => setEditVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setEditing(false); setEditVal(label); } }}
          onBlur={commitRename}
          style={{ width: '100%', padding: '5px 8px', background: 'rgba(255,255,255,0.07)', border: `1px solid ${V.primary}`, borderRadius: 8, color: V.text, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: V.font }}
        />
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
        borderRadius: 10, marginBottom: 2,
        background: active ? `${V.primary}18` : hov ? 'rgba(255,255,255,0.05)' : 'transparent',
        border: active ? `1px solid ${V.primary}35` : '1px solid transparent',
        cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      {icon || (active ? <FolderOpen size={13} color={V.primary} /> : <Folder size={13} color={V.textMuted} />)}
      <span style={{ flex: 1, fontSize: 13, color: active ? V.primary : V.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: V.font }}>
        {label}
      </span>
      {!hov && <span style={{ fontSize: 11, color: V.textMuted, flexShrink: 0, fontFamily: V.font }}>{count}</span>}
      {hov && onRename && (
        <button type="button" onClick={(e) => { e.stopPropagation(); setEditVal(label); setEditing(true); }}
          style={{ background: 'none', border: 'none', color: V.textMuted, cursor: 'pointer', padding: 2, display: 'flex', flexShrink: 0 }}>
          <Pencil size={10} />
        </button>
      )}
      {hov && onDelete && (
        <button type="button" onClick={onDelete}
          style={{ background: 'none', border: 'none', color: '#f5576c', cursor: 'pointer', padding: 2, display: 'flex', flexShrink: 0 }}>
          <Trash2 size={10} />
        </button>
      )}
    </div>
  );
}

// ── Filter chip ────────────────────────────────────────────────
function FilterChip({ active, color, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', padding: '5px 12px',
        borderRadius: 99, fontFamily: V.font, cursor: 'pointer',
        background: active ? `${color}20` : 'transparent',
        border: `1px solid ${active ? color + '55' : V.glassBorder}`,
        color: active ? color : V.textMuted,
        fontSize: 11, fontWeight: active ? 600 : 400,
        whiteSpace: 'nowrap', transition: 'all 0.15s',
      }}>
      {children}
    </button>
  );
}

// ── Asset card ─────────────────────────────────────────────────
function AssetCard({ asset, onDelete, onClick, selected, onSelect, selectionMode }) {
  const [hov, setHov] = useState(false);
  const typeConf = ASSET_TYPE_MAP[asset.asset_type] || { color: '#8c8c8c', label: asset.asset_type };
  const captionStatus = CAPTION_STATUS[asset.caption_status] || CAPTION_STATUS.pending;
  const captionText = asset.caption?.description;

  return (
    <div
      className="asset-card-wrap"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderRadius: 16, overflow: 'hidden', position: 'relative',
        background: 'rgba(255,255,255,0.05)',
        border: `1px solid ${selected ? V.primary : hov ? V.glassBorderHover : V.glassBorder}`,
        cursor: 'pointer', transition: 'border-color 0.15s',
        outline: selected ? `2px solid ${V.primary}40` : 'none',
        contain: 'layout style',
      }}
    >
      {/* Cheap opacity overlay — CSS transition only, no filter repaint */}
      <div className="asset-hover-overlay" />
      <div style={{ position: 'relative', aspectRatio: '4/3', overflow: 'hidden', background: 'rgba(0,0,0,0.3)' }}>
        <LazyImage
          src={asset.url}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          containerStyle={{ width: '100%', height: '100%' }}
        />
        {/* Type badge — opaque bg, no blur */}
        {!selectionMode && !hov && (
          <div style={{
            position: 'absolute', top: 7, left: 7,
            padding: '2px 8px', borderRadius: 99,
            background: typeConf.color,
            color: '#fff', fontSize: 10, fontWeight: 700, fontFamily: V.font,
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.85)', display: 'inline-block' }} />
            {typeConf.label}
          </div>
        )}
        {/* Caption status dot */}
        <div style={{ position: 'absolute', bottom: 7, right: 7, width: 7, height: 7, borderRadius: '50%', background: captionStatus.color, border: '1.5px solid rgba(0,0,0,0.5)' }} title={`Caption: ${asset.caption_status}`} />
        {/* Checkbox — opaque bg, no blur */}
        {(hov || selectionMode) && (
          <div
            onClick={(e) => { e.stopPropagation(); onSelect(e); }}
            style={{
              position: 'absolute', top: 7, left: 7, width: 22, height: 22, borderRadius: 7,
              background: selected ? V.primary : 'rgba(0,0,0,0.75)',
              border: `2px solid ${selected ? V.primary : 'rgba(255,255,255,0.6)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            {selected && <Check size={11} color="#fff" strokeWidth={3} />}
          </div>
        )}
        {/* Delete — opaque bg, no blur */}
        {hov && !selectionMode && (
          <button type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            style={{
              position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: 99,
              background: '#f5576c',
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Trash2 size={11} color="#fff" />
          </button>
        )}
      </div>
      {/* Card info */}
      <div style={{ padding: '10px 12px 12px' }}>
        <div style={{ fontSize: 13, color: V.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, marginBottom: 4, fontFamily: V.font }}>
          {asset.name || typeConf.label}
        </div>
        <div style={{ fontSize: 11, color: V.textMuted, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.45, minHeight: '2.9em', fontFamily: V.font }}>
          {captionText || (asset.caption_status === 'processing' ? 'Generating caption…' : 'Caption pending…')}
        </div>
      </div>
    </div>
  );
}

// ── Selection bar ──────────────────────────────────────────────
function SelectionBar({ count, folders, moveFolderOpen, setMoveFolderOpen, onMove, onDelete, onClear }) {
  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
      transition={{ type: 'spring', damping: 28, stiffness: 340 }}
      style={{
        position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 600,
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
        background: 'rgba(10,4,20,0.96)', border: `1px solid ${V.glassBorder}`,
        borderRadius: 99, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', backdropFilter: 'blur(20px)',
        fontFamily: V.font,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 700, color: V.text, paddingRight: 12, borderRight: `1px solid ${V.glassBorder}`, marginRight: 4, whiteSpace: 'nowrap' }}>
        {count} selected
      </span>

      {/* Move to folder */}
      <div style={{ position: 'relative' }}>
        <button type="button" onClick={() => setMoveFolderOpen((v) => !v)}
          style={{ ...secondaryBtnStyle, padding: '6px 14px', fontSize: 12 }}>
          <FolderMove size={12} /> Move
        </button>
        {moveFolderOpen && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 8px)', left: 0,
            minWidth: 180, background: 'rgba(10,4,20,0.97)', border: `1px solid ${V.glassBorder}`,
            borderRadius: 14, padding: 8, boxShadow: '0 16px 48px rgba(0,0,0,0.7)', zIndex: 700,
          }}>
            {folders.length === 0 ? (
              <div style={{ padding: '8px 10px', color: V.textMuted, fontSize: 12, fontFamily: V.font }}>No folders yet</div>
            ) : folders.map((f) => (
              <button key={f.folder_id} type="button" onClick={() => onMove(f.folder_id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', background: 'transparent', border: 'none', borderRadius: 8, color: V.textSub, fontSize: 13, cursor: 'pointer', fontFamily: V.font, textAlign: 'left' }}>
                <Folder size={12} color={V.textMuted} /> {f.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Delete */}
      <button type="button" onClick={onDelete}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'rgba(245,87,108,0.14)', border: '1px solid rgba(245,87,108,0.35)', borderRadius: 10, color: '#f5576c', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: V.font }}>
        <Trash2 size={12} /> Delete
      </button>

      {/* Clear */}
      <button type="button" onClick={onClear}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: V.textMuted, fontSize: 12, fontFamily: V.font, padding: '0 4px' }}>
        ✕
      </button>
    </motion.div>
  );
}

// ── Empty state ────────────────────────────────────────────────
function EmptyAssets({ onUpload }) {
  return (
    <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 64, textAlign: 'center', gap: 20, minHeight: 360 }}>
      <div style={{ width: 72, height: 72, borderRadius: 99, background: `${V.primary}15`, border: `1px solid ${V.primary}30`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Image size={30} color={V.primary} />
      </div>
      <div>
        <div style={{ ...h2Style, marginBottom: 8 }}>No assets yet</div>
        <div style={{ color: V.textSub, fontSize: 14, maxWidth: 300, lineHeight: 1.6, fontFamily: V.font }}>
          Upload logos, banners, product shots and more. AI uses these during content generation.
        </div>
      </div>
      <button type="button" onClick={onUpload} style={{ ...primaryBtnStyle, padding: '11px 28px', fontSize: 14 }}>
        <Upload size={14} /> Upload Assets
      </button>
    </div>
  );
}
